import React, { useEffect, useMemo, useState } from 'react';
import { Copy, X, AlertTriangle, CheckCircle2, ArrowRight } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import Checkbox from '../ui/Checkbox';
import Badge from '../ui/Badge';
import Dropdown from '../ui/Dropdown';
import { useToast } from '../common/ToastContainer';
import strategyService from '../../services/strategyService';
import { StrategyMetadata } from '../../types';

/** Minimal basket shape used by the target dropdown. Keeps the prop
 *  contract narrow so the dialog accepts both {@link Basket} and the
 *  page-local BasketWithStrategies superset. */
interface BasketOption {
  basket_id: string;
  basket_name: string;
}

interface CopyStrategiesDialogProps {
  isOpen: boolean;
  /** The basket the strategies are sourced from. */
  sourceBasketId: string;
  sourceBasketName: string;
  /** Strategies in the source basket. */
  strategies: StrategyMetadata[];
  /** All baskets the user owns. The source basket is filtered out by the dialog. */
  allBaskets: BasketOption[];
  onClose: () => void;
  /** Called after a successful copy so the parent can refresh / select the target. */
  onCopied?: (targetBasketId: string, copiedCount: number) => void;
}

interface CopyResult {
  target_basket_id: string;
  succeeded: Array<{ source_strategy_id: string; new_strategy_id: string; strategy_name: string }>;
  failed: Array<{ source_strategy_id: string; error: string; message: string }>;
}

/**
 * Multi-select dialog for copying strategies from this basket into another
 * user-owned basket. Wraps the existing single-strategy clone via the new
 * bulk endpoint POST /baskets/{target_id}/copy-strategies.
 *
 * Partial-success contract: if any individual strategy fails (name clash
 * outside auto-rename, alloc cap, etc.), the dialog switches to a result
 * pane listing succeeded vs failed so the user can retry just the failures.
 */
const CopyStrategiesDialog: React.FC<CopyStrategiesDialogProps> = ({
  isOpen,
  sourceBasketId,
  sourceBasketName,
  strategies,
  allBaskets,
  onClose,
  onCopied,
}) => {
  const { showSuccess, showError } = useToast();

  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [targetBasketId, setTargetBasketId] = useState<string>('');
  const [copying, setCopying] = useState(false);
  const [result, setResult] = useState<CopyResult | null>(null);

  // Reset state when dialog opens with a new context.
  useEffect(() => {
    if (isOpen) {
      setSelected(new Set(strategies.map((s) => s.strategyId)));
      setTargetBasketId('');
      setResult(null);
      setCopying(false);
    }
  }, [isOpen, strategies]);

  const targetOptions = useMemo(
    () => allBaskets.filter((b) => b.basket_id !== sourceBasketId),
    [allBaskets, sourceBasketId],
  );

  const targetBasket = useMemo(
    () => targetOptions.find((b) => b.basket_id === targetBasketId),
    [targetOptions, targetBasketId],
  );

  const allSelected = strategies.length > 0 && selected.size === strategies.length;
  const someSelected = selected.size > 0 && !allSelected;

  const toggleAll = () =>
    setSelected(allSelected ? new Set() : new Set(strategies.map((s) => s.strategyId)));

  const toggleOne = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const orderedSelected = useMemo(
    () => strategies.filter((s) => selected.has(s.strategyId)).map((s) => s.strategyId),
    [strategies, selected],
  );

  const canCopy = !!targetBasketId && orderedSelected.length > 0 && !copying;

  const handleCopy = async () => {
    if (!canCopy) return;
    try {
      setCopying(true);
      const data = await strategyService.bulkCopyStrategies(targetBasketId, orderedSelected);
      setResult(data);
      const succeededCount = data.succeeded.length;
      const failedCount = data.failed.length;
      if (failedCount === 0) {
        showSuccess(
          `Copied ${succeededCount} ${succeededCount === 1 ? 'strategy' : 'strategies'} to "${targetBasket?.basket_name || 'target'}"`,
        );
        onCopied?.(targetBasketId, succeededCount);
      } else if (succeededCount === 0) {
        showError(`No strategies copied — all ${failedCount} failed`);
      } else {
        showError(`Copied ${succeededCount}, ${failedCount} failed — see details`);
        onCopied?.(targetBasketId, succeededCount);
      }
    } catch (err: any) {
      showError(err?.message || 'Failed to copy strategies');
    } finally {
      setCopying(false);
    }
  };

  if (!isOpen) return null;

  // Result pane after at least one outcome arrives. The user can either
  // close out, or (when there are failures) reset and try again with a
  // narrower selection.
  const showResultPane = !!result;

  const resetForRetry = () => {
    if (!result) return;
    // Pre-select just the failed ones so retry is one click.
    setSelected(new Set(result.failed.map((f) => f.source_strategy_id)));
    setResult(null);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={copying ? () => {} : onClose}
      maxWidth="lg"
      ariaLabel="Copy strategies to another basket"
      closeOnBackdrop={!copying}
      closeOnEscape={!copying}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white inline-flex items-center gap-2">
            <Copy className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            {showResultPane ? 'Copy results' : 'Copy strategies to another basket'}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 truncate">
            From <span className="font-medium text-gray-700 dark:text-gray-200">{sourceBasketName}</span>
            {targetBasket && (
              <>
                {' '}
                <ArrowRight className="inline h-3 w-3 mx-1 align-text-bottom text-gray-400" />
                {' '}
                <span className="font-medium text-gray-700 dark:text-gray-200">{targetBasket.basket_name}</span>
              </>
            )}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          disabled={copying}
          aria-label="Close"
          className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
        {showResultPane ? (
          <ResultPane result={result!} />
        ) : strategies.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-center">
            <AlertTriangle className="h-8 w-8 text-amber-500 mb-3" />
            <p className="text-sm font-medium text-gray-900 dark:text-white">No strategies to copy</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              This basket has no strategies yet.
            </p>
          </div>
        ) : targetOptions.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-center">
            <AlertTriangle className="h-8 w-8 text-amber-500 mb-3" />
            <p className="text-sm font-medium text-gray-900 dark:text-white">No other baskets available</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Create another basket first to copy strategies into it.
            </p>
          </div>
        ) : (
          <>
            {/* Target basket picker */}
            <div className="space-y-1.5">
              <label className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Target basket
              </label>
              <Dropdown
                value={targetBasketId}
                onChange={(value) => setTargetBasketId(value)}
                options={[
                  { value: '', label: 'Choose a target basket…' },
                  ...targetOptions.map((b) => ({
                    value: b.basket_id,
                    label: b.basket_name || 'Unnamed basket',
                  })),
                ]}
              />
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Strategies are copied as <span className="font-medium">INACTIVE</span>. Names that clash get auto-renamed (<code>(Copy)</code>, <code>(Copy 2)</code>, …).
              </p>
            </div>

            {/* Select-all bar */}
            <label className="flex items-center justify-between gap-3 px-3 py-2 rounded-md bg-gray-50 dark:bg-gray-700/40 border border-gray-200 dark:border-gray-700 cursor-pointer">
              <span className="inline-flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => { if (el) el.indeterminate = someSelected; }}
                  onChange={toggleAll}
                  className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                  Select all
                </span>
              </span>
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                {selected.size} of {strategies.length} selected
              </span>
            </label>

            {/* Strategy list */}
            <div className="space-y-2">
              {strategies.map((s) => {
                const checked = selected.has(s.strategyId);
                return (
                  <label
                    key={s.strategyId}
                    className={`block rounded-lg border p-3 cursor-pointer transition-colors ${
                      checked
                        ? 'border-blue-300 dark:border-blue-700 bg-blue-50/50 dark:bg-blue-900/10'
                        : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/30'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <Checkbox checked={checked} onChange={() => toggleOne(s.strategyId)} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {s.strategyName || 'Unnamed strategy'}
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                          {s.underlying && <Badge variant="info" size="sm">{s.underlying}</Badge>}
                          {s.expiryType && <Badge variant="default" size="sm">{s.expiryType.toUpperCase()}</Badge>}
                          {s.product && <Badge variant="default" size="sm">{s.product}</Badge>}
                          {s.tradingType && <Badge variant="default" size="sm">{s.tradingType}</Badge>}
                          <span>
                            {s.legCount} {s.legCount === 1 ? 'leg' : 'legs'}
                            {s.entryTime && s.exitTime && ` · ${s.entryTime}–${s.exitTime}`}
                          </span>
                        </div>
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between gap-3 px-6 py-3 border-t border-gray-200 dark:border-gray-700">
        {showResultPane ? (
          <>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {result!.failed.length === 0
                ? 'All strategies copied'
                : `${result!.succeeded.length} succeeded · ${result!.failed.length} failed`}
            </span>
            <div className="flex items-center gap-2">
              {result!.failed.length > 0 && (
                <Button variant="outline" size="sm" onClick={resetForRetry}>
                  Retry failed
                </Button>
              )}
              <Button size="sm" onClick={onClose}>Done</Button>
            </div>
          </>
        ) : (
          <>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {!targetBasketId
                ? 'Pick a target basket'
                : selected.size === 0
                  ? 'Pick at least one strategy'
                  : `${selected.size} of ${strategies.length} will be copied`}
            </span>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={onClose} disabled={copying}>
                Cancel
              </Button>
              <Button
                size="sm"
                leftIcon={<Copy className="h-4 w-4" />}
                disabled={!canCopy}
                isLoading={copying}
                onClick={handleCopy}
              >
                {copying ? 'Copying…' : `Copy ${selected.size || ''}`.trim()}
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

const ResultPane: React.FC<{ result: CopyResult }> = ({ result }) => (
  <div className="space-y-3">
    {result.succeeded.length > 0 && (
      <div>
        <div className="text-xs font-medium uppercase tracking-wider text-emerald-700 dark:text-emerald-400 mb-2 inline-flex items-center gap-1.5">
          <CheckCircle2 className="h-3.5 w-3.5" />
          Succeeded ({result.succeeded.length})
        </div>
        <ul className="space-y-1.5">
          {result.succeeded.map((s) => (
            <li
              key={s.new_strategy_id}
              className="text-sm text-gray-700 dark:text-gray-200 px-3 py-2 rounded-md bg-emerald-50 dark:bg-emerald-900/10 border border-emerald-200 dark:border-emerald-800"
            >
              {s.strategy_name}
            </li>
          ))}
        </ul>
      </div>
    )}
    {result.failed.length > 0 && (
      <div>
        <div className="text-xs font-medium uppercase tracking-wider text-red-700 dark:text-red-400 mb-2 inline-flex items-center gap-1.5">
          <AlertTriangle className="h-3.5 w-3.5" />
          Failed ({result.failed.length})
        </div>
        <ul className="space-y-1.5">
          {result.failed.map((f) => (
            <li
              key={f.source_strategy_id}
              className="text-sm px-3 py-2 rounded-md bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800"
            >
              <div className="font-medium text-gray-900 dark:text-white">{f.source_strategy_id}</div>
              <div className="text-xs text-red-700 dark:text-red-300 mt-0.5">{f.message || f.error}</div>
            </li>
          ))}
        </ul>
      </div>
    )}
  </div>
);

export default CopyStrategiesDialog;
